import { Loader } from "@components/Loader";
import { usePeopleFinder } from "@hooks/peopleFinder/usePeopleFinder";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { Mail01, Phone } from "untitledui-js/react";

interface ContactPersonProps {
  email: string;
}

export const ContactPerson = ({ email }: ContactPersonProps) => {
  const { data, error, isLoading } = usePeopleFinder({
    searchTerm: email,
  });

  if (isLoading) {
    return <Loader />;
  }

  const person =
    data?.response && data.response.length > 0 ? data?.response[0] : null;

  if (!person) {
    // When person cannot be found, we log a warning for debugging purposes.
    // We don't share which email was used to avoid leaking personal data in logs.
    console.warn('User not found');
  }

  if (error || !person) {
    return null;
  }

  return (
    <div
      data-component="contact-person"
      className="flex items-center gap-4.5 bg-tertiary-50 p-4 lg:gap-6 dark:bg-secondary-900"
    >
      <div className="flex aspect-square h-20 w-20 items-center justify-center rounded-full bg-tertiary-100 lg:h-24 lg:w-24 dark:bg-white/20">
        <Typography
          size="lg"
          className="leading-[130%] font-bold text-tertiary-300 dark:text-tertiary-200"
        >
          {person.firstName.charAt(0)}
        </Typography>
      </div>
      <div className="flex flex-col gap-1 lg:gap-2">
        <div>
          <Typography
            as="h4"
            className="leading-[150%] text-secondary-700 lg:text-lg dark:text-white"
          >
            {person?.firstName} {person?.lastName}
          </Typography>
          {person?.opcoFunctionTitle && (
            <Typography
              size="sm"
              className="leading-[150%] font-light text-tertiary-900 lg:text-base dark:text-tertiary-100"
            >
              {person.opcoFunctionTitle}
            </Typography>
          )}
        </div>
        {person?.ponMobilePhone && (
          <div className="flex items-center gap-1">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
              <Phone
                className="h-3 w-3 stroke-secondary-700"
                data-testid="phone-icon"
              />
            </div>
            <a
              href={`tel:${person.ponMobilePhone}`}
              className="text-sm leading-[150%] font-light text-tertiary-900 lg:text-base dark:text-tertiary-100"
            >
              {person.ponMobilePhone}
            </a>
          </div>
        )}
        {person?.opcoEmailAccount && (
          <div className="flex items-center gap-1">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
              <Mail01
                className="h-3 w-3 stroke-secondary-700"
                data-testid="mail-icon"
              />
            </div>
            <a
              href={`mailto:${person?.opcoEmailAccount}`}
              className="text-sm leading-[150%] font-light text-tertiary-900 lg:text-base dark:text-tertiary-100"
            >
              {person?.opcoEmailAccount}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
